import { useCallback, useLayoutEffect, useMemo, useRef, useState } from "react";
import stripLeadingGiai from "@/utils/stripLeadingGiai";
import "@/components/LichThiDauFull/LeagueFilter.css";

const CHIP_LIMIT = 10;

export function leagueKey(league) {
  return String(league?.id ?? league?.slug ?? league?.name ?? "");
}

export function leagueLabel(league) {
  return stripLeadingGiai(league?.nameVN || league?.name || "");
}

function LeagueLogo({ src, label }) {
  const [broken, setBroken] = useState(false);
  const show = Boolean(src) && !broken;
  const letter = String(label || "?").trim().charAt(0).toUpperCase() || "?";
  return (
    <span className={`kbv-ltd-lg-logo ${show ? "" : "is-fallback"}`}>
      {show ? (
        <img src={src} alt="" onError={() => setBroken(true)} />
      ) : (
        <span>{letter}</span>
      )}
    </span>
  );
}

function ChipSkeleton() {
  return (
    <span className="kbv-ltd-lg-skel" aria-hidden="true">
      <span className="kbv-ltd-lg-skel-dot" />
      <span className="kbv-ltd-lg-skel-bar" />
    </span>
  );
}

/**
 * @param {object} props
 * @param {object[]} props.leagues
 * @param {string|null} [props.selectedId]
 * @param {(id: string) => void} [props.onSelect]
 * @param {boolean} [props.loading]
 * @param {boolean} [props.compact]
 */
export default function CountryLeagueFilter({
  leagues = [],
  selectedId = null,
  onSelect,
  loading = false,
  compact = false,
}) {
  const rowRef = useRef(null);
  const selectedChipRef = useRef(null);

  const selectedKey = selectedId != null ? String(selectedId) : "";

  const isSelected = useCallback(
    (league) => {
      if (!selectedKey) return false;
      return leagueKey(league) === selectedKey;
    },
    [selectedKey],
  );

  const orderedLeagues = useMemo(() => {
    const seen = new Set();
    const list = [];
    const push = (league) => {
      if (!league) return;
      const key = leagueKey(league);
      if (!key || seen.has(key)) return;
      seen.add(key);
      list.push(league);
    };

    const hot = [];
    const rest = [];
    for (const league of leagues) {
      if (Number(league?.isHot) === 1) hot.push(league);
      else rest.push(league);
    }
    hot.forEach(push);
    rest.forEach(push);

    return list;
  }, [leagues]);

  const chips = useMemo(() => {
    const top = orderedLeagues.slice(0, CHIP_LIMIT);
    if (!selectedKey) return top;
    if (top.some((league) => leagueKey(league) === selectedKey)) return top;
    const selected = orderedLeagues.find((league) => leagueKey(league) === selectedKey);
    return selected ? [...top, selected] : top;
  }, [orderedLeagues, selectedKey]);

  const revealSelectedChip = useCallback((el) => {
    const row = rowRef.current;
    if (!row || !el) return;
    const rowRect = row.getBoundingClientRect();
    const chipRect = el.getBoundingClientRect();
    const padStart = 8;
    const padEnd = 12;
    let delta = 0;
    if (chipRect.left < rowRect.left + padStart) {
      delta = chipRect.left - rowRect.left - padStart;
    } else if (chipRect.right > rowRect.right - padEnd) {
      delta = chipRect.right - rowRect.right + padEnd;
    }
    if (Math.abs(delta) > 1) row.scrollLeft += delta;
  }, []);

  const setSelectedChipRef = useCallback(
    (node) => {
      selectedChipRef.current = node;
      if (node) revealSelectedChip(node);
    },
    [revealSelectedChip],
  );

  useLayoutEffect(() => {
    const el = selectedChipRef.current;
    if (!el) return;
    revealSelectedChip(el);
    const id = requestAnimationFrame(() => revealSelectedChip(el));
    return () => cancelAnimationFrame(id);
  }, [chips, revealSelectedChip, selectedKey]);

  const handleSelect = (league) => {
    const id = leagueKey(league);
    if (!id) return;
    onSelect?.(id);
  };

  const showSkeleton = loading && !chips.length;
  if (!showSkeleton && !leagues.length && !loading) return null;

  return (
    <div className={`kbv-ltd-lg is-country${compact ? " is-compact" : ""}`}>
      <div className="kbv-ltd-lg-bar">
        <div ref={rowRef} className="kbv-ltd-lg-row" role="tablist">
          {showSkeleton
            ? Array.from({ length: 7 }, (_, i) => <ChipSkeleton key={i} />)
            : chips.map((league) => {
                const selected = isSelected(league);
                const label = leagueLabel(league);
                return (
                  <button
                    key={leagueKey(league)}
                    ref={selected ? setSelectedChipRef : undefined}
                    type="button"
                    role="tab"
                    title={label}
                    aria-selected={selected}
                    onClick={() => handleSelect(league)}
                    className={`kbv-ltd-lg-chip ${selected ? "is-active" : ""}`}
                  >
                    <LeagueLogo src={league.logo} label={label} />
                    <span className="kbv-ltd-lg-chip-name">{label}</span>
                  </button>
                );
              })}
        </div>
      </div>
    </div>
  );
}
